<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  rentalId: { type: Number, required: true },
  endpoint: { type: String, default: null },
  title:    { type: String, default: 'Sign Rental Agreement' },
})

const emit = defineEmits(['signed', 'close'])

const canvas = ref(null)
const canvasWrap = ref(null)
const hasSigned = ref(false)
const submitting = ref(false)
const error = ref(null)

let ctx, drawing = false, lastX = 0, lastY = 0

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content

const sizeCanvas = () => {
  if (!canvasWrap.value || !canvas.value) return
  canvas.value.width = canvasWrap.value.clientWidth
  canvas.value.height = canvasWrap.value.clientHeight
  // Re-apply context settings after resize (canvas reset clears context)
  ctx.strokeStyle = '#1a1a1a'
  ctx.lineWidth = 2.5
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
}

onMounted(() => {
  ctx = canvas.value.getContext('2d')
  ctx.strokeStyle = '#1a1a1a'
  ctx.lineWidth = 2.5
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  sizeCanvas()
  window.addEventListener('resize', sizeCanvas)
})

const getPos = (e) => {
  const rect = canvas.value.getBoundingClientRect()
  const src  = e.touches ? e.touches[0] : e
  return { x: src.clientX - rect.left, y: src.clientY - rect.top }
}

const startDraw = (e) => {
  e.preventDefault()
  drawing = true
  const { x, y } = getPos(e)
  lastX = x; lastY = y
}

const draw = (e) => {
  e.preventDefault()
  if (!drawing) return
  const { x, y } = getPos(e)
  ctx.beginPath()
  ctx.moveTo(lastX, lastY)
  ctx.lineTo(x, y)
  ctx.stroke()
  lastX = x; lastY = y
  hasSigned.value = true
}

const stopDraw = () => { drawing = false }

const clear = () => {
  ctx.clearRect(0, 0, canvas.value.width, canvas.value.height)
  hasSigned.value = false
  error.value = null
}

const confirm = async () => {
  if (!hasSigned.value || submitting.value) return
  submitting.value = true
  error.value = null

  try {
    const dataUrl = canvas.value.toDataURL('image/png')
    const body = new FormData()
    body.append('signature', dataUrl)
    body.append('authenticity_token', csrfToken())
    const url = props.endpoint || `/rentals/${props.rentalId}/agreement/sign`
    const res = await fetch(url, { method: 'POST', body })
    if (!res.ok) throw new Error(`Server error ${res.status}`)
    emit('signed')
  } catch (e) {
    error.value = 'Failed to save signature. Please try again.'
  } finally {
    submitting.value = false
  }
}

const onKeydown = (e) => { if (e.key === 'Escape') emit('close') }
onMounted(() => document.addEventListener('keydown', onKeydown))
onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown)
  window.removeEventListener('resize', sizeCanvas)
})
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex md:items-center md:justify-center md:p-4 bg-black/60"
    @click.self="$emit('close')"
  >
    <div class="w-full h-full md:h-auto md:max-w-lg md:rounded-[var(--radius-lg)] md:shadow-[var(--shadow-lg)] bg-[color:var(--surface)] flex flex-col">
      <!-- Header -->
      <div class="px-5 py-4 md:px-6 md:py-5 border-b border-[color:var(--line)] flex items-center justify-between flex-shrink-0">
        <h2 class="text-base font-semibold text-[color:var(--ink)]">
          {{ title }}
        </h2>
        <button
          type="button"
          class="text-[color:var(--muted)] hover:text-[color:var(--ink)] text-xl leading-none"
          @click="$emit('close')"
        >
          ×
        </button>
      </div>

      <!-- Body (scrollable) -->
      <div class="px-5 py-4 md:px-6 md:py-5 flex-1 overflow-y-auto space-y-4">
        <p class="text-sm text-[color:var(--inkSoft)]">
          Draw your signature below using your mouse or finger.
        </p>

        <div
          ref="canvasWrap"
          class="flex-1 min-h-[180px] md:min-h-[200px] border-2 border-dashed border-[color:var(--line)] rounded-[var(--radius-md)] overflow-hidden bg-[color:var(--surfaceAlt)]"
        >
          <canvas
            ref="canvas"
            class="block w-full h-full touch-none cursor-crosshair"
            @mousedown="startDraw"
            @mousemove="draw"
            @mouseup="stopDraw"
            @mouseleave="stopDraw"
            @touchstart="startDraw"
            @touchmove="draw"
            @touchend="stopDraw"
          />
        </div>

        <p
          v-if="error"
          class="text-sm text-[color:var(--danger)]"
        >
          {{ error }}
        </p>
      </div>

      <!-- Footer (sticky) -->
      <div class="px-5 md:px-6 pt-3 pb-[max(env(safe-area-inset-bottom),1rem)] md:pb-5 border-t border-[color:var(--line)] flex gap-3 justify-end flex-shrink-0">
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium text-[color:var(--ink)] border border-[color:var(--line)] rounded-[var(--radius-md)] hover:bg-[color:var(--surfaceAlt)] transition"
          @click="clear"
        >
          Clear
        </button>
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium text-[color:var(--inkSoft)] border border-[color:var(--line)] rounded-[var(--radius-md)] hover:bg-[color:var(--surfaceAlt)] transition"
          @click="$emit('close')"
        >
          Cancel
        </button>
        <button
          type="button"
          class="px-4 py-2 text-sm font-semibold rounded-[var(--radius-md)] disabled:opacity-50 transition hover:opacity-90"
          style="background-color: var(--accent); color: var(--accentInk)"
          :disabled="!hasSigned || submitting"
          @click="confirm"
        >
          {{ submitting ? 'Saving…' : 'Confirm' }}
        </button>
      </div>
    </div>
  </div>
</template>
